import React, { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Self-contained bulk product upload form. Downloads template, uploads CSV,
 * renders per-row errors. Calls `onCreated()` after a successful upload so
 * the parent can refresh dashboards (counts, etc.).
 */
export default function BulkUpload({ onCreated }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/admin/products/bulk-upload/template`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "afripantry-products-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[bulk-upload] template download failed:", err);
      toast.error("Download failed");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Pick a CSV file"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/products/bulk-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.created} product${data.created === 1 ? "" : "s"} created`);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed");
    }
    setUploading(false);
  };

  return (
    <div data-testid="admin-bulk-panel" className="max-w-3xl">
      <div className="card-surface p-6 mb-4">
        <h2 className="font-heading text-xl font-bold mb-1">Bulk product upload</h2>
        <p className="text-sm text-[#5C5C54] mb-4">
          Upload a CSV to create multiple products across vendors in one go. Match each row to a vendor using their{" "}
          <code className="bg-[#F2EFE9] px-1 rounded text-xs">vendor_slug</code>.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            data-testid="bulk-download-template-btn"
            onClick={downloadTemplate}
            className="btn-secondary text-xs"
          >
            Download CSV template
          </button>
        </div>

        <div className="border-2 border-dashed border-[#E5E2D9] rounded-2xl p-6 text-center mb-4">
          <input
            data-testid="bulk-file-input"
            type="file"
            accept=".csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="block mx-auto text-sm"
          />
          {file && (
            <p className="text-xs text-[#5C5C54] mt-3">
              Selected: <strong>{file.name}</strong> ({Math.round(file.size / 1024)} KB)
            </p>
          )}
        </div>

        <button
          data-testid="bulk-upload-submit-btn"
          onClick={submit}
          disabled={!file || uploading}
          className="btn-primary w-full disabled:opacity-50"
        >
          {uploading ? "Uploading…" : "Upload products"}
        </button>

        <p className="text-xs text-[#5C5C54] mt-4">
          Required columns: <code className="bg-[#F2EFE9] px-1 rounded">vendor_slug, name, category, price</code>. Optional:{" "}
          <code className="bg-[#F2EFE9] px-1 rounded">description, stock_quantity, cultural_tags, image_url, is_featured</code>.
          Use a pipe (<code>|</code>) or comma inside cultural_tags.
        </p>
      </div>

      {result && (
        <div className="card-surface p-5" data-testid="bulk-result">
          <p className="font-heading font-bold mb-2">Result</p>
          <p className="text-sm">
            <strong>{result.created}</strong> of {result.total_rows} rows created.
          </p>
          {result.errors?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-bold text-[#A94438] mb-1">Errors ({result.errors.length}):</p>
              <ul className="text-xs text-[#5C5C54] space-y-0.5 max-h-48 overflow-y-auto">
                {result.errors.map((e) => (
                  <li key={`bulk-err-${e.row}`}>Row {e.row}: {e.error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
